"use client";

import {
  getIs2FAVerified,
  logoutAction,
  recreateSession,
} from "@/actions/auth/login";
import { MerchantSessionPayload, SessionPayload } from "@/lib/types";
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { cookieToInitialState } from "wagmi";

import { WagmiProvider } from "wagmi";
import { getWagmiConfig } from "../wagmiConfig";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { isActionError } from "@/utils/isActionError";
import WebSocketClient from "./Dashboard/components/WSClient"; // NotificationData,
import { Crisp } from "crisp-sdk-web";
import { CrispChat } from "@/app/CrispChat";
import { useSessionManager } from "./useSessionManager";
import { useRouter } from "next/navigation";
import { NotificationData } from "@/app/api/address-webhook/interface";
import { socket } from "./Dashboard/components/socketSingleton";

export const queryClient = new QueryClient();

export interface Session {
  user: SessionPayload | MerchantSessionPayload | null;
  isAuthenticated?: boolean;
  is2FAVerified?: boolean;
}

// Define the context type
interface SessionContextType {
  session: Session;
  signOut: () => void;
  signIn: (session: Session) => void;
  recentSuccessfulTransactions?: NotificationData["data"][];
  pushSuccessfulTransaction?: (transactions: NotificationData["data"]) => void;
  removeSuccessfulTransaction: (transactionId: string) => void;
  revalidateSession: () => void;
  revalidate2FA: () => void;
}

// Create the context with a default value
const SessionContext = createContext<SessionContextType | undefined>(undefined);

// SessionProvider props
interface SessionProviderProps {
  children: ReactNode;
  session: Session;
  cookie?: string | null;
  is2FAVerified?: boolean;
}

// Create the provider
export const SessionProvider: React.FC<SessionProviderProps> = ({
  children,
  session: serverSession,
  cookie,
}) => {
  const is2FAVerified = serverSession.is2FAVerified;

  const [session, setSession] = useState<Session>({
    user: serverSession.user,
    isAuthenticated: !!serverSession.user || false,
    is2FAVerified,
  });

  useEffect(() => {
    if (!serverSession.user) return;
    setSession((prev) => ({
      ...prev,
      user: serverSession.user,
      isAuthenticated: !!serverSession.user,
      is2FAVerified: serverSession.is2FAVerified,
    }));
  }, [serverSession]);

  function crispLogout() {
    // Execute this sequence when your users are logging out
    Crisp.setTokenId(); // 1. Clear the token value
    Crisp.session.reset(); // 2. Unbind the current session
  }

  const [recentSuccessfulTransactions, _setRecentSuccessfulTransactions] =
    useState<NotificationData["data"][]>([]);

  const pushSuccessfulTransaction = (
    notificationData: NotificationData["data"]
  ) => {
    _setRecentSuccessfulTransactions((prev) => [...prev, notificationData]);
  };

  const removeSuccessfulTransaction = (transactionId: string) => {
    _setRecentSuccessfulTransactions((prev) =>
      prev.filter((tx) => tx?.txId !== transactionId)
    );
  };

  const initialState = cookieToInitialState(getWagmiConfig(), cookie);

  const { push } = useRouter();

  const signOut = useCallback(
    async (reason?: string) => {
      crispLogout();
      setSession({ user: null, isAuthenticated: false });
      await logoutAction(reason);

      socket.disconnect();
      push("/login" + reason ? `?logout-reason=${reason}` : "");
    },
    [push]
  );

  const signIn = (session: Session) => {
    setSession({ user: session.user, isAuthenticated: true });
  };

  const revalidateSession = useCallback(async () => {
    if (!session.user?.id) return;
    if (session.user.isMerchant) return;

    const result = await recreateSession(session.user?.id);

    if (result && !isActionError(result)) {
      return setSession((prev) => ({
        ...prev,
        user: result.user,
        isAuthenticated: !!result.user,
      }));
    }

    signOut("session-expired-due-to-inactivity&recreate-session=failed");
  }, [session.user?.id, session.user?.isMerchant, signOut]);

  const revalidate2FA = async () => {
    const result = await getIs2FAVerified();

    console.log(result, "result");

    if (!isActionError(result)) {
      return setSession((prev) => ({
        ...prev,
        is2FAVerified: result.is2FAVerified,
      }));
    }
  };

  const mounted = useRef(false);

  useEffect(() => {
    if (mounted.current) {
      return;
    }
    revalidateSession();
    mounted.current = true;
  }, [revalidateSession]);

  useSessionManager(session.user, revalidateSession);

  return (
    <WagmiProvider config={getWagmiConfig()} initialState={initialState}>
      <QueryClientProvider client={queryClient}>
        <SessionContext.Provider
          value={{
            session,
            signOut,
            signIn,
            recentSuccessfulTransactions,
            pushSuccessfulTransaction,
            removeSuccessfulTransaction,
            revalidateSession,
            revalidate2FA,
          }}
        >
          {children}
          {/* <SSEClient /> */}
          <WebSocketClient />

          <CrispChat />
        </SessionContext.Provider>
      </QueryClientProvider>
    </WagmiProvider>
  );
};

// Custom hook to use the session context
export const useSession = (): SessionContextType => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error("useSession must be used within a SessionProvider");
  }
  return context;
};

export const useSession2 = () => {
  const context = useContext(SessionContext);
  return context;
};
